import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useAuth} from '../../contexts/AuthContext';
import {LoadingSpinner} from './LoadingSpinner';
import {Result, Button} from 'antd';
import {LockOutlined} from '@ant-design/icons';

type UserRole = 'super_admin' | 'admin' | 'manager' | 'employee';

interface ProtectedRouteProps {
    children: React.ReactElement;
    requiredRole?: UserRole;
    requiredRoles?: UserRole[];
    requirePermission?: UserRole;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
                                                                  children,
                                                                  requiredRole,
                                                                  requiredRoles,
                                                                  requirePermission,
                                                              }) => {
    const {isAuthenticated, isLoading, user, hasRole, hasPermission} = useAuth();
    const location = useLocation();

    if (isLoading) {
        return <LoadingSpinner message="Checking authentication..."/>;
    }

    if (!isAuthenticated) {
        // Redirect to login page but save the location they were trying to access
        return <Navigate to="/login" state={{from: location}} replace/>;
    }

    // Check role-based access
    if (requiredRole && !hasRole(requiredRole)) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="Sorry, you don't have permission to access this page."
                icon={<LockOutlined style={{color: 'var(--error-color)'}}/>}
                extra={
                    <Button type="primary" onClick={() => window.history.back()}>
                        Go Back
                    </Button>
                }
            />
        );
    }

    if (requiredRoles && !hasRole(requiredRoles)) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="Sorry, you don't have permission to access this page."
                icon={<LockOutlined style={{color: 'var(--error-color)'}}/>}
                extra={
                    <Button type="primary" onClick={() => window.history.back()}>
                        Go Back
                    </Button>
                }
            />
        );
    }

    if (requirePermission && !hasPermission(requirePermission)) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="Sorry, you don't have permission to access this page."
                icon={<LockOutlined style={{color: 'var(--error-color)'}}/>}
                extra={
                    <Button type="primary" onClick={() => window.history.back()}>
                        Go Back
                    </Button>
                }
            />
        );
    }

    // For employees, only allow access to Overview, My Properties, Focus, Sales, and their own profile
    const employeeAllowedPaths = ['/overview', '/my-properties', '/focus', "/sales"];
    const isOwnEmployeePage = location.pathname === `/employees/${user?.id}`;
    if (user?.role === 'employee' && !employeeAllowedPaths.includes(location.pathname) && !isOwnEmployeePage) {
        return <Navigate to="/overview" replace/>;
    }

    return children;
};
